import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, ToggleButton, ToggleButtonGroup, Select, MenuItem, CircularProgress, Alert,
  IconButton, Tooltip, Slider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { bloomberg } from '../theme/bloombergTheme';
import { getModelRotation, RotationPayload, RotationPoint, RotationQuadrant } from '../services/api';

/**
 * Where each theme sits against a benchmark and which way it is moving — a
 * relative-rotation view (backend rotation.py). Right = stronger than the
 * benchmark than of late; up = that strength rising. Themes tend to travel
 * clockwise: improving → leading → weakening → lagging.
 *
 * Scroll to zoom, drag to pan, and replay the trails week by week.
 */

type Scope = 'themes' | 'subs' | 'all';
type View = { cx: number; cy: number; s: number };   // centre and half-width, in chart units
type TrailPoint = RotationPoint['tail'][number];
type Drawn = { p: RotationPoint; trail: TrailPoint[]; head: TrailPoint; q: RotationQuadrant };

const Q: Record<RotationQuadrant, { label: string; color: string; tint: string; says: string }> = {
  improving: { label: 'Improving', color: '#3b82f6', tint: 'rgba(59,130,246,0.14)', says: 'Behind the benchmark, closing the gap' },
  leading: { label: 'Leading', color: '#22c55e', tint: 'rgba(34,197,94,0.14)', says: 'Ahead of the benchmark and pulling away' },
  weakening: { label: 'Weakening', color: '#f59e0b', tint: 'rgba(245,158,11,0.14)', says: 'Still ahead, but losing pace' },
  lagging: { label: 'Lagging', color: '#ef4444', tint: 'rgba(239,68,68,0.14)', says: 'Behind and falling further back' },
};
const CYCLE: RotationQuadrant[] = ['improving', 'leading', 'weakening', 'lagging'];
const MONO = bloomberg.font.mono;
const HEAD = bloomberg.font.heading;

// Chart geometry (viewBox units). The plot is square so rings stay round.
const W = 720, PL = 52, PR = 14, PT = 14, PB = 46;
const PLOT = W - PL - PR;
const H = PT + PLOT + PB;
const MM = 112;            // minimap size

const pct = (v?: number | null) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(1)}%`);
const quadrantOf = (x: number, y: number): RotationQuadrant => (x >= 100 ? (y >= 100 ? 'leading' : 'weakening') : (y >= 100 ? 'improving' : 'lagging'));
const shortDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const longDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const niceStep = (s: number) => [0.25, 0.5, 1, 2, 2.5, 5, 10, 20].find((c) => c >= (2 * s) / 8) || 20;
const dist = (p: { x: number; y: number }) => Math.hypot(p.x - 100, p.y - 100);

function clampView(v: View, home: View): View {
  const lim = home.s * 2;
  return { s: v.s, cx: Math.min(100 + lim, Math.max(100 - lim, v.cx)), cy: Math.min(100 + lim, Math.max(100 - lim, v.cy)) };
}
/** Zoom by factor f keeping the chart point under (px, py) fixed. */
function zoomAt(v: View, px: number, py: number, f: number, home: View): View {
  const dx = v.cx - v.s + ((px - PL) / PLOT) * 2 * v.s;
  const dy = v.cy - v.s + ((PT + PLOT - py) / PLOT) * 2 * v.s;
  const s = Math.min(home.s * 1.5, Math.max(0.4, v.s * f));
  const r = s / v.s;
  return clampView({ cx: dx - (dx - v.cx) * r, cy: dy - (dy - v.cy) * r, s }, home);
}
/** Smooth path through points (Catmull-Rom as cubic Béziers). */
function smooth(pts: [number, number][]) {
  if (pts.length < 2) return '';
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    d += ` C${p1[0] + (p2[0] - p0[0]) / 6},${p1[1] + (p2[1] - p0[1]) / 6} ${p2[0] - (p3[0] - p1[0]) / 6},${p2[1] - (p3[1] - p1[1]) / 6} ${p2[0]},${p2[1]}`;
  }
  return d;
}

const eyebrow = { fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: bloomberg.text.muted };
const card = { bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 2 };

function QPill({ q, small }: { q: RotationQuadrant; small?: boolean }) {
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.6, px: small ? 0.75 : 1, py: small ? 0.1 : 0.25, borderRadius: 10,
      bgcolor: Q[q].tint, fontSize: small ? 11 : 12, fontWeight: 600, color: bloomberg.text.primary, whiteSpace: 'nowrap',
    }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: Q[q].color }} />
      {Q[q].label}
    </Box>
  );
}

/** Plain-language reading of a theme's position, with its own numbers. */
function Explain({ p }: { p: RotationPoint }) {
  const bench = p.benchmark;
  const gap = p.x - 100;
  const prev = p.tail.length >= 5 ? p.tail[p.tail.length - 5].x : null;
  const verdict = {
    leading: `ahead of ${bench} and pulling further ahead`,
    weakening: `still ahead of ${bench}, but the lead is shrinking`,
    lagging: `behind ${bench} and falling further behind`,
    improving: `behind ${bench}, but closing the gap`,
  }[p.quadrant];
  const row = (k: string, v: string, body: React.ReactNode) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '84px 1fr', gap: 1, py: 0.75, borderTop: `1px solid ${bloomberg.border.primary}` }}>
      <Box>
        <Typography sx={{ ...eyebrow, fontSize: 9.5 }}>{k}</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 17, fontWeight: 600, color: bloomberg.text.primary, lineHeight: 1.2 }}>{v}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary, lineHeight: 1.5 }}>{body}</Typography>
    </Box>
  );
  return (
    <Box sx={{ mt: 1.25 }}>
      <Typography sx={{ ...eyebrow, mb: 0.5 }}>Why it sits here</Typography>
      {row('Strength', p.x.toFixed(1), <>Against {bench}, {p.label} is {Math.abs(gap).toFixed(1)}% {gap >= 0 ? 'above' : 'below'} its own 10-week average, so it plots {gap >= 0 ? 'right' : 'left'} of centre.</>)}
      {row('Momentum', p.y.toFixed(1), prev !== null
        ? <>Strength four weeks ago was {prev.toFixed(1)}. <span style={{ fontFamily: MONO }}>{p.x.toFixed(1)} ÷ {prev.toFixed(1)} × 100 = {p.y.toFixed(1)}</span>, so it plots {p.y >= 100 ? 'above' : 'below'} centre.</>
        : <>Strength is {p.y >= 100 ? 'higher' : 'lower'} than four weeks ago, so it plots {p.y >= 100 ? 'above' : 'below'} centre.</>)}
      <Box sx={{ mt: 0.5, p: 1, borderRadius: 1, bgcolor: Q[p.quadrant].tint, borderLeft: `3px solid ${Q[p.quadrant].color}` }}>
        <Typography sx={{ fontSize: 12.5, color: bloomberg.text.primary }}>In short: {verdict}.</Typography>
      </Box>
    </Box>
  );
}

/** Small clockwise-cycle diagram for the explainer. */
function CycleDiagram() {
  const arc = (d: string, c: string) => <path d={d} fill="none" stroke={c} strokeWidth={2.4} strokeLinecap="round" markerEnd="url(#rrg-cycle-arrow)" />;
  return (
    <svg width={112} height={112} viewBox="0 0 104 104" role="img" aria-label="Themes usually move clockwise through the quadrants" style={{ flexShrink: 0 }}>
      <defs>
        <marker id="rrg-cycle-arrow" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill={bloomberg.text.secondary} />
        </marker>
      </defs>
      <rect x={2} y={2} width={100} height={100} rx={8} fill="none" stroke={bloomberg.border.secondary} />
      <rect x={3} y={3} width={49} height={49} rx={6} fill={Q.improving.tint} />
      <rect x={52} y={3} width={49} height={49} rx={6} fill={Q.leading.tint} />
      <rect x={52} y={52} width={49} height={49} rx={6} fill={Q.weakening.tint} />
      <rect x={3} y={52} width={49} height={49} rx={6} fill={Q.lagging.tint} />
      {arc('M22,52 A30,30 0 0 1 52,22', Q.improving.color)}
      {arc('M52,22 A30,30 0 0 1 82,52', Q.leading.color)}
      {arc('M82,52 A30,30 0 0 1 52,82', Q.weakening.color)}
      {arc('M52,82 A30,30 0 0 1 22,52', Q.lagging.color)}
      <text x={7} y={14} fontSize={8} fontWeight={700} fill={Q.improving.color}>IMPROVING</text>
      <text x={97} y={14} fontSize={8} fontWeight={700} fill={Q.leading.color} textAnchor="end">LEADING</text>
      <text x={97} y={97} fontSize={8} fontWeight={700} fill={Q.weakening.color} textAnchor="end">WEAKENING</text>
      <text x={7} y={97} fontSize={8} fontWeight={700} fill={Q.lagging.color}>LAGGING</text>
    </svg>
  );
}

export default function RotationMap({ onOpen }: { onOpen: (id: number) => void }) {
  const [benchmark, setBenchmark] = useState('ACWI');
  const [tail, setTail] = useState(6);
  const [scope, setScope] = useState<Scope>('themes');
  const [parent, setParent] = useState<string>('');
  const [data, setData] = useState<RotationPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [hoverQ, setHoverQ] = useState<RotationQuadrant | null>(null);
  const [view, setView] = useState<View>({ cx: 100, cy: 100, s: 5 });
  const [dragging, setDragging] = useState(false);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [showHelp, setShowHelp] = useState<boolean>(() => {
    try { return localStorage.getItem('rotation.help') !== 'hidden'; } catch { return true; }
  });
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ x: number; y: number; v: View; moved: boolean } | null>(null);
  const justDragged = useRef(false);

  useEffect(() => {
    setLoading(true); setError(null);
    getModelRotation(benchmark, tail)
      .then(setData)
      .catch(() => setError('Could not load the rotation map.'))
      .finally(() => setLoading(false));
  }, [benchmark, tail]);

  const parents = useMemo(() => {
    const m = new Map<string, string>();
    (data?.points || []).forEach((p) => { if (p.kind === 'theme' && !p.theme_parent && p.theme) m.set(p.theme, p.label); });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);

  const points = useMemo(() => (data?.points || []).filter((p) => {
    if (scope === 'all') return p.kind === 'theme';
    if (scope === 'themes') return p.kind === 'theme' && !p.theme_parent;
    return p.kind === 'theme' && !!p.theme_parent && (!parent || p.theme_parent === parent);
  }), [data, scope, parent]);

  // Home view: centred on 100/100, wide enough for every dot and trail.
  const home = useMemo<View>(() => {
    let m = 3;
    points.forEach((p) => p.tail.forEach((t) => { m = Math.max(m, Math.abs(t.x - 100), Math.abs(t.y - 100)); }));
    return { cx: 100, cy: 100, s: m * 1.1 };
  }, [points]);
  const homeRef = useRef(home);
  homeRef.current = home;
  useEffect(() => { setView(home); }, [home]);

  // Replay: step 0 = oldest week of the trail, weeks-1 = this week.
  const weeks = useMemo(() => Math.max(1, ...points.map((p) => p.tail.length)), [points]);
  const dates = useMemo(() => points.find((p) => p.tail.length === weeks)?.tail.map((t) => t.date) || [], [points, weeks]);
  useEffect(() => { setStep(weeks - 1); setPlaying(false); }, [weeks, data]);
  const lag = weeks - 1 - step;
  useEffect(() => {
    if (!playing) return undefined;
    if (step >= weeks - 1) { setPlaying(false); return undefined; }
    const t = setTimeout(() => setStep((s) => s + 1), 900);
    return () => clearTimeout(t);
  }, [playing, step, weeks]);

  // Wheel zoom around the cursor (native listener so the page doesn't scroll).
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = svg.getBoundingClientRect();
      const px = ((e.clientX - r.left) * W) / r.width, py = ((e.clientY - r.top) * H) / r.height;
      setView((v) => zoomAt(v, px, py, Math.exp(e.deltaY * 0.0015), homeRef.current));
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    justDragged.current = false;
    drag.current = { x: e.clientX, y: e.clientY, v: view, moved: false };
    const move = (ev: PointerEvent) => {
      const d = drag.current, svg = svgRef.current;
      if (!d || !svg) return;
      const k = W / svg.getBoundingClientRect().width;
      const dx = (ev.clientX - d.x) * k, dy = (ev.clientY - d.y) * k;
      if (!d.moved && Math.hypot(dx, dy) < 4) return;
      if (!d.moved) { d.moved = true; setDragging(true); }
      const u = (2 * d.v.s) / PLOT;
      setView(clampView({ cx: d.v.cx - dx * u, cy: d.v.cy + dy * u, s: d.v.s }, homeRef.current));
    };
    const up = () => {
      justDragged.current = !!drag.current?.moved;
      drag.current = null;
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const svgPoint = (e: React.MouseEvent): [number, number] => {
    const r = svgRef.current!.getBoundingClientRect();
    return [((e.clientX - r.left) * W) / r.width, ((e.clientY - r.top) * H) / r.height];
  };
  const zoomBy = (f: number) => setView((v) => zoomAt(v, PL + PLOT / 2, PT + PLOT / 2, f, home));
  const fitTo = (tl: { x: number; y: number }[]) => {
    const xs = tl.map((t) => t.x), ys = tl.map((t) => t.y);
    const s = Math.max(0.8, (Math.max(...xs) - Math.min(...xs)) / 2, (Math.max(...ys) - Math.min(...ys)) / 2) * 1.5;
    setView(clampView({ cx: (Math.max(...xs) + Math.min(...xs)) / 2, cy: (Math.max(...ys) + Math.min(...ys)) / 2, s: Math.min(s, home.s) }, home));
  };
  const zoomQuadrant = (q: RotationQuadrant) => {
    const h = home.s / 2;
    setView({ cx: 100 + (q === 'leading' || q === 'weakening' ? h : -h), cy: 100 + (q === 'leading' || q === 'improving' ? h : -h), s: h * 1.08 });
  };

  const sx = (x: number) => PL + ((x - (view.cx - view.s)) / (2 * view.s)) * PLOT;
  const sy = (y: number) => PT + PLOT - ((y - (view.cy - view.s)) / (2 * view.s)) * PLOT;
  const X0 = sx(100), Y0 = sy(100);
  const zoomed = view.s < home.s * 0.98 || Math.abs(view.cx - 100) > 0.01 || Math.abs(view.cy - 100) > 0.01;
  const ticks = useMemo(() => {
    const st = niceStep(view.s);
    const make = (c: number) => {
      const out: number[] = [];
      for (let v = Math.ceil((c - view.s) / st) * st; v <= c + view.s + 1e-9; v += st) out.push(Math.round(v * 100) / 100);
      return out;
    };
    return { st, xs: make(view.cx), ys: make(view.cy) };
  }, [view]);
  const fmtTick = (v: number) => (ticks.st < 1 ? v.toFixed(ticks.st < 0.5 ? 2 : 1) : String(v));
  const ringPx = (ticks.st * 2 * PLOT) / (2 * view.s);
  const rings = Array.from({ length: 12 }, (_, i) => (i + 1) * ringPx).filter((r) => r < PLOT * 1.5);

  // Each theme as of the replay week.
  const drawn = useMemo<Drawn[]>(() => points.flatMap((p) => {
    const trail = p.tail.slice(0, Math.max(0, p.tail.length - lag));
    if (!trail.length) return [];
    const head = trail[trail.length - 1];
    return [{ p, trail, head, q: quadrantOf(head.x, head.y) }];
  }), [points, lag]);
  const counts = useMemo(() => {
    const c: Record<RotationQuadrant, number> = { leading: 0, weakening: 0, lagging: 0, improving: 0 };
    drawn.forEach((d) => { c[d.q] += 1; });
    return c;
  }, [drawn]);
  const inView = drawn.filter((d) => Math.abs(d.head.x - view.cx) <= view.s && Math.abs(d.head.y - view.cy) <= view.s);
  const isFocus = (d: Drawn) => {
    if (hover !== null) return d.p.id === hover || d.p.id === selected;
    if (hoverQ) return d.q === hoverQ;
    return selected === null || d.p.id === selected;
  };
  const showLabel = (d: Drawn) => inView.length <= 22 || d.p.id === selected || d.p.id === hover || (lag === 0 && d.p.quadrant !== d.p.prev_quadrant);

  const moves = (data?.moves || []).filter((m) => points.some((p) => p.id === m.id));
  const sel = points.find((p) => p.id === selected) || null;
  const hov = drawn.find((d) => d.p.id === hover) || null;
  const toggleHelp = () => {
    const next = !showHelp;
    setShowHelp(next);
    try { localStorage.setItem('rotation.help', next ? 'shown' : 'hidden'); } catch { /* storage unavailable */ }
  };
  const trailPath = (tl: TrailPoint[]) => {
    const seq: RotationQuadrant[] = [];
    tl.forEach((t) => { const q = quadrantOf(t.x, t.y); if (seq[seq.length - 1] !== q) seq.push(q); });
    return seq;
  };
  const weekLabel = dates[step] ? longDate(dates[step]) : data?.as_of ? longDate(data.as_of) : '—';
  const benchLabel = benchmark === 'source' ? 'own source ETF' : benchmark === 'SPY' ? 'S&P 500' : 'ACWI';
  const corners: Record<RotationQuadrant, [number, number]> = {
    leading: [PL + PLOT, PT], weakening: [PL + PLOT, PT + PLOT], lagging: [PL, PT + PLOT], improving: [PL, PT],
  };

  // Minimap (shown when zoomed): the home view with the current window marked.
  const mx0 = PL + PLOT - MM - 10, my0 = PT + PLOT - MM - 10;
  const mX = (x: number) => mx0 + ((x - (100 - home.s)) / (2 * home.s)) * MM;
  const mY = (y: number) => my0 + MM - ((y - (100 - home.s)) / (2 * home.s)) * MM;
  const clampM = (v: number, lo: number) => Math.min(lo + MM, Math.max(lo, v));

  const cornerTag = (q: RotationQuadrant, x: number, y: number, anchor: 'start' | 'end') => {
    const text = `${Q[q].label.toUpperCase()}  ${counts[q]}`;
    const w = text.length * 7.4 + 18;
    const rx = anchor === 'end' ? x - w : x;
    return (
      <g key={q}>
        <rect x={rx} y={y - 13} width={w} height={22} rx={11} fill={bloomberg.bg.secondary} fillOpacity={0.85} stroke={Q[q].color} strokeOpacity={0.45} />
        <circle cx={rx + 11} cy={y - 2} r={3.5} fill={Q[q].color} />
        <text x={rx + 20} y={y + 2} fontSize={11} fontWeight={700} letterSpacing="0.1em" fill={Q[q].color}>{Q[q].label.toUpperCase()}</text>
        <text x={rx + w - 9} y={y + 2} fontSize={11} fontWeight={600} textAnchor="end" fill={bloomberg.text.secondary} fontFamily={MONO}>{counts[q]}</text>
      </g>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Typography sx={{ ...eyebrow, color: bloomberg.accent.orange, mb: 0.25 }}>Relative rotation</Typography>
          <Typography sx={{ fontFamily: HEAD, fontSize: 22, fontWeight: 700, color: bloomberg.text.primary, lineHeight: 1.2 }}>Where is money rotating?</Typography>
          <Typography sx={{ fontSize: 12.5, color: bloomberg.text.muted, mt: 0.25 }}>
            Each theme against {benchmark === 'source' ? 'its own source ETF' : benchLabel}: the dot is this week, the trail the {tail - 1} weeks before.
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => v && setScope(v)}>
          <ToggleButton value="themes" sx={{ textTransform: 'none', fontSize: 12, px: 1.5 }}>Themes</ToggleButton>
          <ToggleButton value="subs" sx={{ textTransform: 'none', fontSize: 12, px: 1.5 }}>Sub-themes</ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: 12, px: 1.5 }}>All</ToggleButton>
        </ToggleButtonGroup>
        {scope === 'subs' && (
          <Select size="small" value={parent} displayEmpty onChange={(e) => setParent(e.target.value as string)} sx={{ fontSize: 12, minWidth: 170 }}
            inputProps={{ 'aria-label': 'Sub-themes of' }}>
            <MenuItem value="">All sub-themes</MenuItem>
            {parents.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </Select>
        )}
        <Select size="small" value={benchmark} onChange={(e) => setBenchmark(e.target.value as string)} sx={{ fontSize: 12, minWidth: 150 }}
          inputProps={{ 'aria-label': 'Against' }}>
          <MenuItem value="ACWI">Against ACWI (world)</MenuItem>
          <MenuItem value="SPY">Against S&amp;P 500</MenuItem>
          <MenuItem value="source">Against its own ETF</MenuItem>
        </Select>
        <Select size="small" value={tail} onChange={(e) => setTail(Number(e.target.value))} sx={{ fontSize: 12 }}
          inputProps={{ 'aria-label': 'Trail length' }}>
          {[4, 6, 8, 12].map((w) => <MenuItem key={w} value={w}>{w}-week trails</MenuItem>)}
        </Select>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* Chart card */}
        <Box sx={{ ...card, flex: '0 1 800px', width: '100%', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.1, borderBottom: `1px solid ${bloomberg.border.primary}`, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={eyebrow}>vs {benchLabel} · week ending</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontFamily: MONO, fontSize: 14, fontWeight: 600, color: bloomberg.text.primary }}>{weekLabel}</Typography>
                {lag > 0 && (
                  <Box component="span" sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', px: 0.75, py: 0.1, borderRadius: 0.5,
                    bgcolor: bloomberg.accent.orangeDim, color: bloomberg.accent.orangeLight }}>
                    REPLAY · {lag}W AGO
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${bloomberg.border.secondary}`, borderRadius: 1, overflow: 'hidden' }}>
              <Tooltip title="Zoom out"><IconButton size="small" onClick={() => zoomBy(1.5)} aria-label="Zoom out" sx={{ borderRadius: 0 }}><RemoveIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              <Typography sx={{ fontSize: 11.5, fontFamily: MONO, color: bloomberg.text.secondary, minWidth: 44, textAlign: 'center',
                borderLeft: `1px solid ${bloomberg.border.secondary}`, borderRight: `1px solid ${bloomberg.border.secondary}`, lineHeight: '30px' }}>
                {Math.round((home.s / view.s) * 100)}%
              </Typography>
              <Tooltip title="Zoom in"><IconButton size="small" onClick={() => zoomBy(1 / 1.5)} aria-label="Zoom in" sx={{ borderRadius: 0 }}><AddIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              <Tooltip title="Show everything"><span>
                <IconButton size="small" onClick={() => setView(home)} disabled={!zoomed} aria-label="Reset zoom"
                  sx={{ borderRadius: 0, borderLeft: `1px solid ${bloomberg.border.secondary}` }}><CenterFocusStrongIcon sx={{ fontSize: 16 }} /></IconButton>
              </span></Tooltip>
            </Box>
          </Box>

          <Box sx={{ position: 'relative', px: 1, pt: 1 }}>
            {(loading || !data) && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Relative rotation map"
              onPointerDown={onPointerDown}
              onDoubleClick={(e) => { const [px, py] = svgPoint(e); setView((v) => zoomAt(v, px, py, 0.5, home)); }}
              style={{ display: 'block', opacity: loading ? 0.4 : 1, userSelect: 'none', fontFamily: bloomberg.font.body,
                cursor: dragging ? 'grabbing' : 'grab', touchAction: zoomed ? 'none' : 'pan-y', transition: 'opacity .2s' }}>
              <defs>
                <clipPath id="rrg-clip"><rect x={PL} y={PT} width={PLOT} height={PLOT} rx={6} /></clipPath>
                {CYCLE.map((q) => (
                  <radialGradient key={q} id={`rrg-q-${q}`} gradientUnits="userSpaceOnUse" cx={corners[q][0]} cy={corners[q][1]} r={PLOT * 1.05}>
                    <stop offset="0" stopColor={Q[q].color} stopOpacity={0.17} />
                    <stop offset="0.7" stopColor={Q[q].color} stopOpacity={0.04} />
                    <stop offset="1" stopColor={Q[q].color} stopOpacity={0.015} />
                  </radialGradient>
                ))}
                <filter id="rrg-shadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="1" stdDeviation="1.6" floodColor="#000" floodOpacity="0.55" />
                </filter>
                {drawn.map(({ p, trail, q }) => trail.length > 1 && (
                  <linearGradient key={p.id} id={`rrg-t-${p.id}`} gradientUnits="userSpaceOnUse"
                    x1={sx(trail[0].x)} y1={sy(trail[0].y)} x2={sx(trail[trail.length - 1].x)} y2={sy(trail[trail.length - 1].y)}>
                    <stop offset="0" stopColor={Q[q].color} stopOpacity={0.08} />
                    <stop offset="1" stopColor={Q[q].color} stopOpacity={0.95} />
                  </linearGradient>
                ))}
              </defs>

              {/* Background: quadrant washes, grid, distance rings, crosshair */}
              <rect x={PL} y={PT} width={PLOT} height={PLOT} rx={6} fill={bloomberg.bg.primary}
                onClick={() => { if (!justDragged.current) setSelected(null); }} />
              <g clipPath="url(#rrg-clip)" pointerEvents="none">
                <rect x={X0} y={-1e4} width={2e4} height={Math.max(0, Y0 + 1e4)} fill="url(#rrg-q-leading)" />
                <rect x={X0} y={Y0} width={2e4} height={2e4} fill="url(#rrg-q-weakening)" />
                <rect x={-1e4} y={Y0} width={Math.max(0, X0 + 1e4)} height={2e4} fill="url(#rrg-q-lagging)" />
                <rect x={-1e4} y={-1e4} width={Math.max(0, X0 + 1e4)} height={Math.max(0, Y0 + 1e4)} fill="url(#rrg-q-improving)" />
                {ticks.xs.map((t) => t !== 100 && <line key={`x${t}`} x1={sx(t)} y1={PT} x2={sx(t)} y2={PT + PLOT} stroke={bloomberg.chart.grid} strokeWidth={0.7} />)}
                {ticks.ys.map((t) => t !== 100 && <line key={`y${t}`} x1={PL} y1={sy(t)} x2={PL + PLOT} y2={sy(t)} stroke={bloomberg.chart.grid} strokeWidth={0.7} />)}
                {rings.map((r) => <circle key={r} cx={X0} cy={Y0} r={r} fill="none" stroke={bloomberg.text.muted} strokeOpacity={0.14} strokeDasharray="2 5" />)}
                <line x1={X0} y1={PT} x2={X0} y2={PT + PLOT} stroke={bloomberg.text.muted} strokeOpacity={0.55} strokeWidth={1} />
                <line x1={PL} y1={Y0} x2={PL + PLOT} y2={Y0} stroke={bloomberg.text.muted} strokeOpacity={0.55} strokeWidth={1} />
                <circle cx={X0} cy={Y0} r={3} fill={bloomberg.text.muted} />
              </g>
              <rect x={PL} y={PT} width={PLOT} height={PLOT} rx={6} fill="none" stroke={bloomberg.border.secondary} pointerEvents="none" />

              {/* Axes */}
              <g pointerEvents="none" fontFamily={MONO} fontSize={10}>
                {ticks.xs.map((t) => sx(t) > PL + 2 && sx(t) < PL + PLOT - 2 && (
                  <g key={`xl${t}`}>
                    <line x1={sx(t)} y1={PT + PLOT} x2={sx(t)} y2={PT + PLOT + 4} stroke={bloomberg.border.secondary} />
                    <text x={sx(t)} y={PT + PLOT + 16} fill={t === 100 ? bloomberg.text.primary : bloomberg.text.muted} textAnchor="middle" fontWeight={t === 100 ? 600 : 400}>{fmtTick(t)}</text>
                  </g>
                ))}
                {ticks.ys.map((t) => sy(t) > PT + 2 && sy(t) < PT + PLOT - 2 && (
                  <g key={`yl${t}`}>
                    <line x1={PL - 4} y1={sy(t)} x2={PL} y2={sy(t)} stroke={bloomberg.border.secondary} />
                    <text x={PL - 7} y={sy(t) + 3} fill={t === 100 ? bloomberg.text.primary : bloomberg.text.muted} textAnchor="end" fontWeight={t === 100 ? 600 : 400}>{fmtTick(t)}</text>
                  </g>
                ))}
              </g>
              <text x={PL + PLOT / 2} y={H - 8} textAnchor="middle" fontSize={10} fontWeight={700} letterSpacing="0.14em" fill={bloomberg.text.muted} pointerEvents="none">
                RELATIVE STRENGTH  →
              </text>
              <text transform={`translate(13 ${PT + PLOT / 2}) rotate(-90)`} textAnchor="middle" fontSize={10} fontWeight={700} letterSpacing="0.14em" fill={bloomberg.text.muted} pointerEvents="none">
                MOMENTUM  →
              </text>

              {/* Quadrant tags */}
              <g pointerEvents="none">
                {X0 < PL + PLOT - 150 && Y0 > PT + 40 && cornerTag('leading', PL + PLOT - 10, PT + 24, 'end')}
                {X0 < PL + PLOT - 170 && Y0 < PT + PLOT - 40 && !zoomed && cornerTag('weakening', PL + PLOT - 10, PT + PLOT - 14, 'end')}
                {X0 > PL + 150 && Y0 < PT + PLOT - 40 && cornerTag('lagging', PL + 10, PT + PLOT - 14, 'start')}
                {X0 > PL + 160 && Y0 > PT + 40 && cornerTag('improving', PL + 10, PT + 24, 'start')}
              </g>

              {/* Trails and heads */}
              <g clipPath="url(#rrg-clip)">
                {drawn.map((d) => {
                  const { p, trail, head, q } = d;
                  const c = Q[q].color;
                  const isSel = selected === p.id;
                  const hx = sx(head.x), hy = sy(head.y);
                  const pts = trail.map((t) => [sx(t.x), sy(t.y)] as [number, number]);
                  const n = trail.length;
                  const r = isSel ? 8 : 6.5;
                  const prev = pts[n - 2];
                  let arrow: string | null = null;
                  if (prev) {
                    const vx = hx - prev[0], vy = hy - prev[1], len = Math.hypot(vx, vy);
                    if (len > 3) {
                      const ux = vx / len, uy = vy / len;
                      const bx = hx + ux * (r + 2), by = hy + uy * (r + 2);
                      arrow = `M${hx + ux * (r + 9)},${hy + uy * (r + 9)} L${bx - uy * 4.5},${by + ux * 4.5} L${bx + uy * 4.5},${by - ux * 4.5} Z`;
                    }
                  }
                  const moved = lag === 0 && p.quadrant !== p.prev_quadrant;
                  return (
                    <g key={p.id} opacity={isFocus(d) ? 1 : 0.16} style={{ cursor: 'pointer', transition: 'opacity .15s' }}
                      onMouseEnter={() => setHover(p.id)} onMouseLeave={() => setHover(null)}
                      onClick={(e) => { e.stopPropagation(); if (justDragged.current) return; setSelected(isSel ? null : p.id); }}>
                      {n > 1 && <path d={smooth(pts)} fill="none" stroke={`url(#rrg-t-${p.id})`} strokeWidth={isSel ? 3 : 2.2} strokeLinecap="round" />}
                      {pts.slice(0, -1).map(([x, y], i) => (
                        <circle key={i} cx={x} cy={y} r={isSel ? 3.2 : 2.3} fill={bloomberg.bg.primary} stroke={c} strokeWidth={1.3}
                          strokeOpacity={0.25 + (0.65 * (i + 1)) / n} />
                      ))}
                      {isSel && trail.slice(0, -1).map((t, i) => (
                        <text key={`t${i}`} x={pts[i][0] + 6} y={pts[i][1] - 6} fontSize={9.5} fill={bloomberg.text.muted} fontFamily={MONO}
                          stroke={bloomberg.bg.primary} strokeWidth={3} paintOrder="stroke">{shortDate(t.date)}</text>
                      ))}
                      <circle cx={hx} cy={hy} r={r + 7} fill={c} fillOpacity={isSel ? 0.22 : 0.12} />
                      {moved && <circle cx={hx} cy={hy} r={r + 5} fill="none" stroke={c} strokeWidth={1.2} strokeDasharray="3 3" />}
                      {arrow && <path d={arrow} fill={c} />}
                      <circle cx={hx} cy={hy} r={r} fill={c} stroke={isSel ? bloomberg.text.primary : bloomberg.bg.primary} strokeWidth={2} filter="url(#rrg-shadow)" />
                    </g>
                  );
                })}
                {/* Labels on top of every trail */}
                {drawn.map((d) => {
                  if (!showLabel(d)) return null;
                  const { p, head, q } = d;
                  const isSel = selected === p.id;
                  const hx = sx(head.x), hy = sy(head.y);
                  const lw = p.label.length * 6.4 + 16;
                  const left = hx + 16 + lw > PL + PLOT - 4;
                  const lx = left ? hx - 16 - lw : hx + 16;
                  return (
                    <g key={`l${p.id}`} opacity={isFocus(d) ? 1 : 0.16} style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHover(p.id)} onMouseLeave={() => setHover(null)}
                      onClick={(e) => { e.stopPropagation(); if (justDragged.current) return; setSelected(isSel ? null : p.id); }}>
                      <rect x={lx} y={hy - 10} width={lw} height={20} rx={10} fill={bloomberg.bg.secondary} fillOpacity={0.94}
                        stroke={Q[q].color} strokeOpacity={isSel ? 1 : 0.4} />
                      <text x={lx + 8} y={hy + 4} fontSize={11.5} fontWeight={isSel ? 700 : 500} fill={bloomberg.text.primary}>{p.label}</text>
                    </g>
                  );
                })}
              </g>

              {/* Minimap */}
              {zoomed && (
                <g onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    const [px, py] = svgPoint(e);
                    const cx = 100 - home.s + ((px - mx0) / MM) * 2 * home.s;
                    const cy = 100 - home.s + ((my0 + MM - py) / MM) * 2 * home.s;
                    setView((v) => clampView({ ...v, cx, cy }, home));
                  }} style={{ cursor: 'pointer' }}>
                  <rect x={mx0} y={my0} width={MM} height={MM} rx={6} fill={bloomberg.bg.secondary} fillOpacity={0.94} stroke={bloomberg.border.secondary} />
                  <line x1={mX(100)} y1={my0} x2={mX(100)} y2={my0 + MM} stroke={bloomberg.text.muted} strokeOpacity={0.4} />
                  <line x1={mx0} y1={mY(100)} x2={mx0 + MM} y2={mY(100)} stroke={bloomberg.text.muted} strokeOpacity={0.4} />
                  {drawn.map((d) => <circle key={d.p.id} cx={mX(d.head.x)} cy={mY(d.head.y)} r={2} fill={Q[d.q].color} />)}
                  <rect x={clampM(mX(view.cx - view.s), mx0)} y={clampM(mY(view.cy + view.s), my0)}
                    width={Math.max(4, clampM(mX(view.cx + view.s), mx0) - clampM(mX(view.cx - view.s), mx0))}
                    height={Math.max(4, clampM(mY(view.cy - view.s), my0) - clampM(mY(view.cy + view.s), my0))}
                    fill={bloomberg.accent.orange} fillOpacity={0.08} stroke={bloomberg.accent.orange} strokeWidth={1.2} />
                </g>
              )}
            </svg>

            {hov && !dragging && (
              <Box sx={{
                position: 'absolute', pointerEvents: 'none', zIndex: 2,
                left: `calc(8px + (100% - 16px) * ${sx(hov.head.x) / W})`,
                top: `calc(8px + (100% - 8px) * ${sy(hov.head.y) / H})`,
                transform: `translate(${sx(hov.head.x) > W * 0.6 ? 'calc(-100% - 18px)' : '18px'}, -50%)`,
                ...card, bgcolor: bloomberg.bg.tertiary, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.45)', width: 250,
              }}>
                <Box sx={{ height: 3, bgcolor: Q[hov.q].color }} />
                <Box sx={{ px: 1.5, py: 1 }}>
                  {hov.p.parent_label && <Typography sx={{ ...eyebrow, fontSize: 9.5 }}>{hov.p.parent_label}</Typography>}
                  <Typography sx={{ fontFamily: HEAD, fontSize: 14, fontWeight: 700, color: bloomberg.text.primary }}>{hov.p.label}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                    <QPill q={hov.q} small />
                    <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>
                      {lag === 0 ? `${hov.p.weeks_in_quadrant} week${hov.p.weeks_in_quadrant > 1 ? 's' : ''} in quadrant` : `week of ${shortDate(hov.head.date)}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mt: 1 }}>
                    {[['Strength', hov.head.x.toFixed(1)], ['Momentum', hov.head.y.toFixed(1)], ['3M', pct(hov.p.returns['3M'])]].map(([k, v]) => (
                      <Box key={k}>
                        <Typography sx={{ ...eyebrow, fontSize: 9 }}>{k}</Typography>
                        <Typography sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, color: bloomberg.text.primary }}>{v}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {hov.trail.length > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      <Typography sx={{ fontSize: 10.5, color: bloomberg.text.muted, mr: 0.25 }}>Since {shortDate(hov.trail[0].date)}</Typography>
                      {trailPath(hov.trail).map((q, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <Typography component="span" sx={{ fontSize: 11, color: bloomberg.text.muted }}>→</Typography>}
                          <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: Q[q].color }} title={Q[q].label} />
                        </React.Fragment>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Playback */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, pt: 0.75, pb: 0.25, borderTop: `1px solid ${bloomberg.border.primary}` }}>
            <Tooltip title={playing ? 'Pause' : 'Replay the trail week by week'}><span>
              <IconButton size="small" aria-label={playing ? 'Pause replay' : 'Replay'} disabled={weeks < 2}
                onClick={() => { if (playing) setPlaying(false); else { if (step >= weeks - 1) setStep(0); setPlaying(true); } }}
                sx={{ bgcolor: bloomberg.accent.orange, color: '#fff', width: 30, height: 30, '&:hover': { bgcolor: bloomberg.accent.orangeLight } }}>
                {playing ? <PauseIcon sx={{ fontSize: 18 }} /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </span></Tooltip>
            <Slider size="small" min={0} max={Math.max(0, weeks - 1)} step={1} value={step} disabled={weeks < 2}
              onChange={(_, v) => { setPlaying(false); setStep(v as number); }}
              marks={dates.map((d, i) => ({ value: i, label: shortDate(d) }))}
              aria-label="Week shown" getAriaValueText={(v) => (dates[v] ? `Week of ${shortDate(dates[v])}` : '')}
              sx={{ flex: 1, mx: 1, mb: 1.5, color: bloomberg.accent.orange,
                '& .MuiSlider-markLabel': { fontSize: 10, fontFamily: MONO, color: bloomberg.text.muted },
                '& .MuiSlider-markLabelActive': { color: bloomberg.text.secondary } }} />
          </Box>
          <Typography sx={{ fontSize: 11, color: bloomberg.text.muted, px: 2, pb: 1.25 }}>
            Scroll to zoom · drag to pan · double-click to zoom in · click a theme for its numbers · dashed ring = changed quadrant this week
          </Typography>
        </Box>

        {/* Side panel */}
        <Box sx={{ flex: 1, minWidth: 290, display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
          {/* Quadrant summary, laid out as on the chart; numbers give the usual rotation order */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
            {(['improving', 'leading', 'lagging', 'weakening'] as RotationQuadrant[]).map((q) => {
              const inQ = drawn.filter((d) => d.q === q).sort((a, b) => dist(b.head) - dist(a.head));
              return (
                <Box key={q} onMouseEnter={() => setHoverQ(q)} onMouseLeave={() => setHoverQ(null)}
                  sx={{ ...card, position: 'relative', p: 1.25, pt: 1, overflow: 'hidden', transition: 'border-color .15s',
                    borderColor: hoverQ === q ? Q[q].color : bloomberg.border.primary,
                    '&::before': { content: '""', position: 'absolute', inset: '0 0 auto 0', height: 3, bgcolor: Q[q].color } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ ...eyebrow, color: Q[q].color }}>{CYCLE.indexOf(q) + 1} · {Q[q].label}</Typography>
                    <Tooltip title={`Zoom into ${Q[q].label}`}>
                      <Box component="button" onClick={() => zoomQuadrant(q)} aria-label={`Zoom into ${Q[q].label}`}
                        sx={{ all: 'unset', cursor: 'pointer', fontSize: 12, color: bloomberg.text.muted, px: 0.5, borderRadius: 0.5,
                          '&:hover': { color: bloomberg.text.primary, bgcolor: bloomberg.bg.hover }, '&:focus-visible': { outline: `2px solid ${Q[q].color}` } }}>
                        ⤢
                      </Box>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                    <Typography sx={{ fontFamily: HEAD, fontSize: 24, fontWeight: 700, lineHeight: 1.15, color: bloomberg.text.primary }}>{inQ.length}</Typography>
                    <Typography sx={{ fontSize: 11, color: bloomberg.text.muted, lineHeight: 1.25 }}>{Q[q].says}</Typography>
                  </Box>
                  <Box sx={{ mt: 0.5, minHeight: 18 }}>
                    {inQ.slice(0, 3).map((d) => (
                      <Typography key={d.p.id} noWrap onClick={() => setSelected(d.p.id)}
                        onMouseEnter={() => setHover(d.p.id)} onMouseLeave={() => setHover(null)}
                        sx={{ fontSize: 12, color: bloomberg.text.secondary, cursor: 'pointer', '&:hover': { color: bloomberg.text.primary } }}>
                        {d.p.label}
                      </Typography>
                    ))}
                    {inQ.length > 3 && <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>+{inQ.length - 3} more</Typography>}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {sel ? (
            <Box sx={{ ...card, overflow: 'hidden' }}>
              <Box sx={{ height: 3, bgcolor: Q[sel.quadrant].color }} />
              <Box sx={{ p: 2 }}>
                {sel.parent_label && <Typography sx={eyebrow}>{sel.parent_label}</Typography>}
                <Typography sx={{ fontFamily: HEAD, fontSize: 18, fontWeight: 700, color: bloomberg.text.primary, lineHeight: 1.25 }}>{sel.label}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                  <QPill q={sel.quadrant} />
                  <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>
                    {sel.weeks_in_quadrant} week{sel.weeks_in_quadrant > 1 ? 's' : ''}
                    {sel.prev_quadrant !== sel.quadrant ? ` · from ${Q[sel.prev_quadrant].label}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.75, mt: 1.5 }}>
                  {(['1W', '1M', '3M', '1Y'] as const).map((k) => {
                    const v = sel.returns[k];
                    return (
                      <Box key={k} sx={{ bgcolor: bloomberg.bg.tertiary, borderRadius: 1, px: 1, py: 0.75 }}>
                        <Typography sx={{ ...eyebrow, fontSize: 9 }}>{k}</Typography>
                        <Typography sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, color: v === null || v === undefined ? bloomberg.text.muted : v >= 0 ? bloomberg.status.positive : bloomberg.status.negative }}>{pct(v)}</Typography>
                      </Box>
                    );
                  })}
                </Box>
                <Explain p={sel} />
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  <Button size="small" variant="contained" disableElevation onClick={() => onOpen(sel.id)} sx={{ textTransform: 'none' }}>Open model →</Button>
                  <Button size="small" variant="outlined" onClick={() => fitTo(sel.tail)} sx={{ textTransform: 'none' }}>Zoom to its trail</Button>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ ...card, borderStyle: 'dashed', p: 2 }}>
              <Typography sx={eyebrow}>Selected theme</Typography>
              <Typography sx={{ fontSize: 13, color: bloomberg.text.secondary, mt: 0.5 }}>
                Click a theme on the map to see its returns and why it sits where it does.
              </Typography>
            </Box>
          )}

          <Box sx={{ ...card, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={eyebrow}>Changed quadrant this week</Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: 12, color: bloomberg.text.muted }}>{moves.length}</Typography>
            </Box>
            {moves.length === 0 && <Typography sx={{ fontSize: 13, color: bloomberg.text.muted }}>None in this view.</Typography>}
            {moves.map((m) => (
              <Box key={m.id} onClick={() => setSelected(m.id)} onMouseEnter={() => setHover(m.id)} onMouseLeave={() => setHover(null)}
                sx={{ py: 0.9, px: 1, mx: -1, borderRadius: 1, cursor: 'pointer', borderTop: `1px solid ${bloomberg.border.primary}`,
                  '&:first-of-type': { borderTop: 'none' }, '&:hover': { bgcolor: bloomberg.bg.hover } }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: bloomberg.text.primary }} noWrap>
                  {m.parent_label && <span style={{ color: bloomberg.text.muted, fontWeight: 500 }}>{m.parent_label} › </span>}{m.label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4 }}>
                  <QPill q={m.from} small />
                  <Typography component="span" sx={{ fontSize: 12, color: bloomberg.text.muted }}>→</Typography>
                  <QPill q={m.to} small />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* How it works */}
      <Box sx={{ ...card, mt: 2, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={eyebrow}>Guide</Typography>
            <Typography sx={{ fontFamily: HEAD, fontSize: 16, fontWeight: 700, color: bloomberg.text.primary }}>How to read this map</Typography>
          </Box>
          <Button size="small" onClick={toggleHelp} sx={{ textTransform: 'none', fontSize: 12 }} aria-expanded={showHelp}>
            {showHelp ? 'Hide' : 'Show'}
          </Button>
        </Box>
        {showHelp && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 3, mt: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: bloomberg.text.primary, mb: 0.5 }}>The dot: this week</Typography>
              <Typography sx={{ fontSize: 12.5, color: bloomberg.text.secondary, lineHeight: 1.65 }}>
                The centre (100, 100) means in line with the benchmark. <b>Right of centre</b>: the theme is doing
                better against the benchmark than over the last 10 weeks. <b>Above centre</b>: that advantage is
                growing. The dotted rings mark distance from the centre: the further out, the stronger the effect.
                The small arrow points the way the theme moved in the last week.
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: bloomberg.text.primary, mb: 0.5 }}>The trail: where it came from</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Typography sx={{ fontSize: 12.5, color: bloomberg.text.secondary, lineHeight: 1.65 }}>
                  Each small ring is the theme's position on an earlier Friday, fading with age. A <b>long trail</b>
                  means it is moving quickly; a <b>tight cluster</b>, little change. Themes usually circle
                  <b> clockwise</b>: a trail curving up from Lagging into Improving is the early sign of a turn, one
                  bending down out of Leading the first sign of fading. Press ▶ to watch it.
                </Typography>
                <CycleDiagram />
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: bloomberg.text.primary, mb: 0.75 }}>How it is calculated</Typography>
              {[
                ['Relative strength', 'RS = theme ÷ benchmark', 'Every Friday. The theme is an equal-weighted index of its holdings, from our stored closing prices.'],
                ['Strength (across)', '100 × RS ÷ avg of RS, 10 wks', '103 means 3% better against the benchmark than its recent norm.'],
                ['Momentum (up)', '100 × strength ÷ strength 4 wks ago', 'Above 100 means strength is rising.'],
              ].map(([t, f, note], i) => (
                <Box key={t} sx={{ display: 'flex', gap: 1.25, mb: 1.1 }}>
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: bloomberg.accent.orangeDim, color: bloomberg.accent.orangeLight, fontSize: 11, fontWeight: 700 }}>{i + 1}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: bloomberg.text.primary }}>{t}</Typography>
                    <Box component="code" sx={{ display: 'inline-block', fontFamily: MONO, fontSize: 11.5, color: bloomberg.text.primary,
                      bgcolor: bloomberg.bg.tertiary, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 0.75, px: 0.75, py: 0.2, my: 0.4 }}>{f}</Box>
                    <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary, lineHeight: 1.5 }}>{note}</Typography>
                  </Box>
                </Box>
              ))}
              <Typography sx={{ fontSize: 11.5, color: bloomberg.text.muted, lineHeight: 1.5 }}>
                Weekly, to {data?.as_of ? longDate(data.as_of) : '—'}. It shows performance relative to the benchmark,
                not whether the theme is up or down in absolute terms.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
